/*
 * Detalles de los productos en el módulo de devoluciones.
 *
 * Componentes que muestran detalles del producto cargados en las distintas listas
 * del módulo de órdenes. De manera individual, así como los que se encuentran
 * dentro de circuitos.
 *
 * Created on Mon Oct 02 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <b-container>
    <b-row>

      <!-- #region::Images carousel -->
      <b-col
        v-if="$ability.can('read', 'Product_images')"
        lg="1"
        md="2"
      >
        <ProductCardSwiper
          :title="product.Name"
          :images-small="product.images.small"
          :images-full="product.images.full"
        />
      </b-col>
      <!-- #endregion::Images carousel -->

      <!-- #region::Main content -->
      <b-col
        lg="11"
        md="10"
      >
        <b-row>

          <!-- #region::Product brand -->
          <b-col
            v-if="$ability.can('read', 'Product_brand')"
            :md="colsNumber"
          >
            <InformationBasicCard
              title="Marca"
              :tags="[{ value: product.name_brand, type: 'string' }]"
            />
          </b-col>
          <!-- #endregion::Product brand -->

          <!-- #region::Product model -->
          <b-col
            v-if="$ability.can('read', 'Product_model')"
            :md="colsNumber"
          >
            <InformationBasicCard
              title="Modelo"
              :tags="[{ value: product.Model, type: 'string' }]"
            />
          </b-col>
          <!-- #endregion::Product model -->

          <!-- #region::Product category -->
          <b-col
            v-if="$ability.can('read', 'Product_category')"
            :md="colsNumber"
          >
            <InformationBasicCard
              title="Categoría y subcategoría"
              :tags="[{ value: `Categoría: ${product.TrueCategory ? product.TrueCategory.Name : 'Sin categoría'}`, type: 'string' },
                      { value: `Subcategoría: ${product.name_categorie}`, type: 'string' }]"
            />
          </b-col>
          <!-- #endregion::Product category -->

          <!-- #region::Product internal code -->
          <b-col
            v-if="product.Type === 'Nuevo' && $ability.can('read', 'Product_code')"
            :md="colsNumber"
          >
            <InformationBasicCard
              title="Código interno"
              :tags="[{ value: product.Code, type: 'string' }]"
            />
          </b-col>
          <!-- #endregion::Product internal code -->

          <!-- #region::Tapestry and paing color product -->
          <b-col
            v-if="$ability.can('read', 'Product_color_paint') && $ability.can('read', 'Product_color_tapestry')"
            :md="colsNumber"
          >
            <InformationBasicCard
              title="Color pintura y tapiz"
              :tags="[{ name: product.NamePaint, value: product.CodePaint, type: 'color' },
                      { name: product.NameTapestry, value: product.CodeTapestry, type: 'color' }]"
            />
          </b-col>
          <!-- #endregion::Tapestry and paing color product -->

          <!-- #region::Product item -->
          <b-col
            v-if="product.Type === 'Nuevo' && $ability.can('read', 'Product_item')"
            :md="colsNumber"
          >
            <InformationBasicCard
              title="Item"
              :tags="[{ value: product.Item, type: 'string' }]"
            />
          </b-col>
          <!-- #endregion::Product item -->

          <!-- #region::Quantity pieces product for refund -->
          <b-col :md="colsNumber">
            <InformationBasicCard
              title="Cantidad"
              :tags="[{ value: product.pieces.length, type: 'string' }]"
            />
          </b-col>
          <!-- #endregion::Quantity pieces product for refund -->

          <!-- #region::Rating level for used product coordinator -->
          <b-col
            v-if="canShowProductRatingLevel"
            :md="colsNumber"
          >
            <InformationBasicCard
              title="Nivel de habilitación"
              :tags="[{ value: formatUsedProductRatingLevel(product.UnitPriceType), type: 'string' }]"
            />
          </b-col>
          <!-- #endregion::Rating level for used product coordinator -->

          <!-- #region::Product prices list -->
          <b-col
            v-if="$ability.can('read', 'Product_prices_list')"
            :md="colsNumber"
          >

            <!-- #region::Used and remanofactured prices -->
            <template v-if="product.Type === 'Usado'">
              <div
                class="mt-1"
              >
                <h5 class="font-weight-bolder">
                  Precios
                </h5>
                <b-form-radio-group
                  v-model="selectedPrice"
                  :options="pricesListOptions"
                  :name="idRadioButtons"
                  :disabled="disabledFormComponents"
                  stacked
                />
              </div>
            </template>
            <!-- #endregion::Used and remanofactured prices -->

            <!-- #region::New product prices -->
            <template v-else>
              <template v-if="activatePricesList">
                <InformationBasicCard
                  v-if="normalPricesDefined"
                  title="Precios"
                  :tags="[{ value: product.PriceMin, prepend: 'Mín:', type: 'currency' },
                          { value: product.PriceMax, prepend: 'Máx:', type: 'currency' },
                          ... product.OfferPrice ? [{ value: product.OfferPrice, prepend: 'Oferta:', type: 'currency', style: 'color: #FF7766;' }] : []]"
                >
                  <template
                    v-if="!disabledFormComponents || isOfferPriceSelected"
                    v-slot:selected-offer-price
                  >
                    <b-form-checkbox
                      v-model="isOfferPriceSelected"
                      style="margin-left: -25px !important;"
                      :disabled="disabledFormComponents"
                    />
                  </template>
                </InformationBasicCard>
                <InformationBasicCard
                  v-else
                  title="Precios"
                  :tags="[{ value: 'Precios por definir', type: 'string' }]"
                />
              </template>
              <template v-if="activateWholesalerPricesList">
                <InformationBasicCard
                  v-if="wholesalerPricesDefined"
                  title="Mayorista"
                  :tags="[{ value: product.PriceMinWholesaler, prepend: 'Mín:', type: 'currency' },
                          { value: product.PriceMaxWholesaler, prepend: 'Máx:', type: 'currency' }]"
                />
                <InformationBasicCard
                  v-else-if="normalPricesDefined"
                  title="Precios"
                  :tags="[{ value: product.PriceMin, prepend: 'Mín:', type: 'currency' },
                          { value: product.PriceMax, prepend: 'Máx:', type: 'currency' }]"
                />
                <InformationBasicCard
                  v-else
                  title="Precios"
                  :tags="[{ value: 'Precios por definir', type: 'string' }]"
                />
              </template>
            </template>
            <!-- #endregion::New product prices -->

          </b-col>
          <!-- #endregion::Product prices list -->

          <!-- #region::Product unit price input -->
          <b-col
            v-if="canShow"
            :md="colsNumber"
          >
            <div class="mt-1">
              <h5 class="font-weight-bolder">
                Precio unitario
              </h5>
              <TextInputWithValidation
                v-model="unitPrice"
                :vid="unitPriceUUID"
                :rules="{
                  required: true,
                  negative,
                  notalpha,
                  decimal,
                  'min_value':valuePrice,
                }"
                type="text"
                name="precio unitario"
                maxlength="12"
                placeholder="100.00"
                prepend="$"
                :styled="false"
                :disabled="disabledFormComponents"
                @input="onUpdateUnitPrice"
              />
            </div>
          </b-col>
          <!-- #endregion::Product unit price input -->

          <!-- #region::Product subtotal -->
          <b-col
            v-if="canShow"
            :md="colsNumber"
          >
            <InformationBasicCard
              title="Subtotal"
              :tags="[{ value: subtotal, type: 'currency' }]"
            />
          </b-col>
          <!-- #endregion::Product subtotal -->

        </b-row>
      </b-col>
      <!-- #endregion::Main content -->

      <!-- #region::Toggle Button -->
      <b-button
        :variant="visible ? 'flat-primary' : 'flat-secondary'"
        class="btn-icon rounded-circle ml-1 toggle-button"
        @click="collapse"
      >
        <feather-icon :icon="iconToggle" />
      </b-button>
      <!-- #endregion::Toggle Button -->

    </b-row>
  </b-container>
</template>

<script>
// #region Imports
import { mapGetters } from 'vuex'
import { extend } from 'vee-validate'
import {
  required, decimal, negative, notalpha,
} from '@validations'
import {
  BRow, BCol, BContainer, BFormRadioGroup, BButton, BFormCheckbox,
} from 'bootstrap-vue'
import { v4 as uuidv4 } from 'uuid'

import validateCurrency from '@/helpers/CurrencyFormat'
import usedProductRatingLevel from '@/helpers/UsedProductRatingLevel'
import ProductCardSwiper from '@/components/swipers/ProductCardSwiper.vue'
import InformationBasicCard from '@/components/cards/InformationBasicCard.vue'
import TextInputWithValidation from '@/components/forms/TextInputWithValidation.vue'
// #endregion

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BContainer,
    BFormCheckbox,
    BFormRadioGroup,
    ProductCardSwiper,
    InformationBasicCard,
    TextInputWithValidation,
  },
  props: {
    /**
     * Contiene la información del producto
     */
    product: {
      type: Object,
      required: true,
    },
    /**
     * Icono del botón de mostrar/ocultar
     */
    iconToggle: {
      type: String,
      default: 'ChevronDownIcon',
    },
    /**
     * Indica si el componente se encuentra visible
     */
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * Indica si los componentes del formulario deben estar deshabilitados
     */
    disabledFormComponents: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem('userData')),
      unitPrice: '',

      pricesListOptions: [],
      selectedPrice: '',
      valuePrice: '',
      isOfferPriceSelected: false,

      /**
       * Tipos de validaciones
       */
      decimal,
      required,
      negative,
      notalpha,

      idRadioButtons: '',
      ownerOrderRole: '',
    }
  },
  computed: {
    ...mapGetters({
      getRefund: 'refunds/getRefund',
      getDetailCircuits: 'orders/getDetailCircuits',
    }),
    /**
     * Información de la devolución
     */
    refund: {
      get() { return this.getRefund },
    },
    /**
     * Circuitos de la orden de devolución
     */
    orderCircuits: {
      get() { return this.getDetailCircuits },
    },
    /**
     * Determina el número de columnas que ocupará el componente
     * de detalles de producto en base al tipo de producto
     */
    colsNumber() {
      return this.product.Type === 'Nuevo' ? 2 : 3
    },
    /**
     * Subtotal del producto
     */
    subtotal() {
      return this.unitPrice * this.product.pieces.length || '0'
    },
    /**
     * UUID para el campo de precio unitario
     */
    unitPriceUUID() {
      return uuidv4()
    },
    /**
     * Circuito al que pertenece el producto
     */
    circuit() {
      const circuit = this.orderCircuits.find(c => c.uuid === this.product.uuidCircuit)

      return circuit ?? null
    },
    /**
     * Determina si se deben mostrar los componentes de precios
     */
    canShow() {
      return this.userData?.Role !== 'Coordinador de Almacen'
    },
    /**
     * Precio máximo sugerido
     */
    maxPrice() {
      if (this.product.MaxSuggestedPrice) {
        return `Precio máximo: ${this.isValidCurrency(this.product.MaxSuggestedPrice)}`
      }

      return 'Precio máximo: sin especificar'
    },
    /**
     * Determina si se deben mostrar los precios de lista
     */
    activatePricesList() {
      if (this.canShowPricesList && this.canShowWholesalerPrices && this.ownerOrderRole === 'Mayorista') {
        return false
      }

      if (this.canShowPricesList && this.canShowWholesalerPrices && this.ownerOrderRole === 'Vendedor') {
        return true
      }

      return this.canShowPricesList
    },
    /**
     * Determina si se deben mostrar los precios mayoristas
     */
    activateWholesalerPricesList() {
      if (this.canShowPricesList && this.canShowWholesalerPrices && this.ownerOrderRole === 'Mayorista') {
        return true
      }

      if (this.canShowPricesList && this.canShowWholesalerPrices && this.ownerOrderRole === 'Vendedor') {
        return false
      }

      return this.canShowWholesalerPrices
    },
    /**
     * Determina si se pueden mostrar los precios de lista
     */
    canShowPricesList() {
      const canShowPricesList = this.$ability.can('read', 'Product_prices_list') && this.baseListPrice
      return canShowPricesList
    },
    /**
     * Determina si se pueden mostrar los precios mayoristas
     */
    canShowWholesalerPrices() {
      const canShowWholesalerPrices = this.$ability.can('read', 'Product_prices_wholesaler') && this.baseWholesalerPrice
      return canShowWholesalerPrices
    },
    /**
     * Determina si los precios de lista están definidos
     */
    normalPricesDefined() {
      return this.product.PriceMin && this.product.PriceMax
    },
    /**
     * Determina si los precios mayoristas están definidos
     */
    wholesalerPricesDefined() {
      return this.product.PriceMinWholesaler && this.product.PriceMaxWholesaler
    },
    /**
     * Determina si se debe mostrar el precio de lista
     */
    baseListPrice() {
      if (this.userData?.Role !== 'Mayorista') {
        return true
      }

      return this.checkPrices
    },
    /**
     * Determina si se debe mostrar el precio mayorista
     */
    baseWholesalerPrice() {
      if (this.userData?.Role !== 'Mayorista') {
        return true
      }

      return !this.checkPrices
    },
    /**
     * Determina si se debe mostrar el precio de lista
     */
    checkPrices() {
      if (!this.normalPricesDefined && this.wholesalerPricesDefined) {
        return false
      }

      if (this.normalPricesDefined && this.wholesalerPricesDefined) {
        return false
      }

      if (!this.normalPricesDefined && !this.wholesalerPricesDefined) {
        return true
      }

      if (this.normalPricesDefined && !this.wholesalerPricesDefined) {
        return true
      }

      return true
    },
    /**
     * Determina si se puede mostrar el nivel de habilitación del producto
     */
    canShowProductRatingLevel() {
      return this.$ability.can('read qualification_level', 'Devolution')
        && this.product.Type === 'Usado'
        && this.product.UnitPriceType
    },
  },
  watch: {
    selectedPrice() {
      if (this.product.Type === 'Usado') {
        switch (this.selectedPrice) {
          case 'PriceLf':
            this.valuePrice = this.product.PriceLf
            break

          case 'PriceDr':
            this.valuePrice = this.product.PriceDr
            break

          default:
            this.valuePrice = this.product.PriceDr + 1
            break
        }

        this.product.unitPriceOrigin = this.valuePrice
        this.product.unitPriceType = this.selectedPrice
      }
    },
  },

  /**
  * Hook de creación del componente
  *
  * @summary Inicializa los valores del componente. Toma en cuenta el tipo de producto y establece
  * los valores de los precios de lista y mayorista. Considera también si el producto es usado y
  * si tiene precio de oferta. En caso de que el producto sea usado, se establece el precio de lista
  * como el precio por defecto. En caso de que el producto sea nuevo o remanufacturado, se establece
  * el precio mínimo como el precio por defecto
  */
  created() {
    if (this.product.Type === 'Usado') {
      this.pricesListOptions = [
        // Used products have different price options
        { text: `L/F: ${this.isValidCurrency(this.product.PriceLf)}`, value: 'PriceLf' },
        { html: `D/R: ${this.isValidCurrency(this.product.PriceDr)} <br/><small>${this.maxPrice}</small>`, value: 'PriceDr' },
        ...this.product.OfferPrice ? [{ html: `<span style="color: #FF7766;">Oferta: ${this.isValidCurrency(this.product.OfferPrice)}</span>`, value: 'OfferPrice' }] : [],
        { text: 'R:', value: 'R' },
      ]

      // this.selectedPrice = 'PriceSt'
      this.selectedPrice = 'PriceLf'
      this.valuePrice = this.product.PriceLf
    } else if (this.product.unitPriceType === 'OfferPrice') {
      // Offer price is only available for new and remanufactured products
      this.isOfferPriceSelected = true
    } else {
      this.checkNewAndRemanufacturedPrices()
      this.product.unitPriceOrigin = this.valuePrice
      this.product.unitPriceType = this.selectedPrice
    }

    extend('min_value', {
      params: ['target'],
      validate(value, { target }) {
        return Number(value) >= Number(target)
      },
      message(field, { target }) {
        return `El campo ${field} debe ser mayor o igual a ${validateCurrency(target)}`
      },
    })

    this.idRadioButtons = uuidv4()

    if (this.$route.name === 'refund-details') {
      if (this.circuit && !this.circuit?.ActiveOfferPrice) {
        this.unitPrice = this.product.unitPrice
        this.valuePrice = this.product.unitPriceOrigin
        this.selectedPrice = this.product.unitPriceType
      } else if (!this.circuit) {
        this.unitPrice = this.product.UnitPrice
        this.valuePrice = this.product.UnitPriceOrigin
        this.selectedPrice = this.product.UnitPriceType
      }

      if (this.selectedPrice === 'OfferPrice') {
        this.isOfferPriceSelected = true
      }

      this.ownerOrderRole = this.refund.devolution.user?.Role || this.userData?.Role
    }
  },
  methods: {
    /**
    * Evento que se ejecuta al actualizar el precio unitario
    *
    * @summary Actualiza el precio unitario del producto cuando se modifica el campo
    * de precio unitario
    */
    onUpdateUnitPrice() {
      this.product.unitPrice = Number(this.unitPrice)
    },

    /**
     * Evento del botón de mostrar/ocultar
     *
     * @summary Emite el evento para mostrar/ocultar el componente
     */
    collapse() {
      this.$emit('toggle-collapse')
    },

    /**
     * Revisión de precios
     *
     * @summary Verifica si el precio de lista es el mínimo o el mayorista
     */
    checkNewAndRemanufacturedPrices() {
      if (this.product.Type === 'Nuevo') {
        this.selectedPrice = this.product.PriceMin ? 'PriceMin' : 'PriceMinWholesaler'
        this.valuePrice = this.product.PriceMin ? this.product.PriceMin : this.product.PriceMinWholesaler
      } else {
        this.selectedPrice = 'PriceMin'
        this.valuePrice = this.product.PriceMin
      }
    },

    /**
     * Formatea el nivel de habilitación del producto
     *
     * @param {string} price Precio del producto
     * @returns {string} Nivel de habilitación del producto
     */
    formatUsedProductRatingLevel(price) {
      return usedProductRatingLevel(price)
    },

    /**
    * Validacion de datos numéricos
    *
    * @summary Valida si los datos numéricos son válidos y se aplica el formato de moneda
    * @param {String} value - Valor a validar
    * @return {String} Valor con formato de moneda
    */
    isValidCurrency(value) {
      return validateCurrency(value)
    },
  },
}
</script>
